import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@monigo/ui';
import type { AdminRole } from '@monigo/shared';
import { Logo } from '../Brand/Logo.js';
import { useSession } from '../../lib/store/session.js';
import { useTheme } from '../../lib/store/theme.js';
import { GuidedTour } from '../Tour/GuidedTour.js';

interface NavItem {
  to: string;
  key: string;
  tourAttr?: string;
  roles?: AdminRole[];
}

const NAV: NavItem[] = [
  { to: '/', key: 'nav.dashboard', tourAttr: 'dashboard' },
  { to: '/customers', key: 'nav.customers', tourAttr: 'customers' },
  { to: '/kyc', key: 'nav.kyc', tourAttr: 'kyc', roles: ['super_admin', 'compliance_reviewer'] },
  { to: '/transfers', key: 'nav.transfers', tourAttr: 'transfers' },
  { to: '/rates', key: 'nav.rates', tourAttr: 'rates', roles: ['super_admin', 'operator'] },
  { to: '/fees', key: 'nav.fees', roles: ['super_admin', 'operator'] },
  { to: '/promo-codes', key: 'nav.promoCodes', roles: ['super_admin', 'operator'] },
  { to: '/staff', key: 'nav.staff', roles: ['super_admin'] },
  { to: '/audit', key: 'nav.audit' },
  { to: '/settings', key: 'nav.settings', roles: ['super_admin', 'operator'] },
];

function SunIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
    </svg>
  );
}

function SidebarContent({ visible, onNavigate }: { visible: NavItem[]; onNavigate?: () => void }) {
  const { t } = useTranslation();
  return (
    <nav className="flex flex-col gap-1 text-sm" aria-label="Sidebar" data-tour="sidebar">
      {visible.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.to === '/'}
          onClick={onNavigate}
          data-tour={n.tourAttr}
          className={({ isActive }) =>
            cn(
              'rounded-md px-3 py-2 text-text-muted hover:bg-surface-elevated',
              isActive && 'bg-surface-elevated font-semibold text-text',
            )
          }
        >
          {t(n.key)}
        </NavLink>
      ))}
    </nav>
  );
}

export function AdminShell() {
  const { t, i18n } = useTranslation();
  const { user, role, signOut } = useSession();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const visible = NAV.filter((n) => !n.roles || (role && n.roles.includes(role)));

  return (
    <div className="min-h-screen bg-background text-text">
      <GuidedTour />
      <div className="flex">
        {/* Sidebar overlay — mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar — desktop (always visible) + mobile (slide-in overlay) */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-50 flex h-screen w-60 flex-col border-r border-border bg-surface/95 px-4 py-6 backdrop-blur transition-transform duration-200',
            // Mobile: slide in/out
            'md:sticky md:translate-x-0',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="relative mb-8 flex items-center justify-between">
            <Logo showBadge={false} />
            {/* Close button — mobile only */}
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="rounded-md p-1 text-text-muted hover:bg-surface-elevated md:hidden"
              aria-label="Close sidebar"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <SidebarContent visible={visible} onNavigate={() => setSidebarOpen(false)} />

          {/* Sidebar footer — theme toggle, language, logout (all screen sizes) */}
          <div className="mt-auto flex flex-col gap-1 border-t border-border pt-4">
            <button
              type="button"
              onClick={toggle}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-text-muted hover:bg-surface-elevated"
            >
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
              {t('common.' + (theme === 'dark' ? 'lightMode' : 'darkMode'))}
            </button>
            <button
              type="button"
              onClick={() => i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr')}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-text-muted hover:bg-surface-elevated"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {i18n.language.toUpperCase()}
            </button>
            {user ? (
              <button
                type="button"
                onClick={async () => {
                  await signOut();
                  navigate('/login');
                }}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-text-muted hover:bg-surface-elevated"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {t('common.logout')}
              </button>
            ) : null}
          </div>
        </aside>

        <div className="flex-1">
          {/* Clean header — only hamburger + logo on mobile; role badge on desktop */}
          <header className="flex items-center justify-between border-b border-border bg-surface/80 px-4 py-3 md:px-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="rounded-md p-1 text-text-muted hover:bg-surface-elevated md:hidden"
                aria-label="Open sidebar"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div className="md:hidden">
                <Logo />
              </div>
            </div>
            <div className="flex items-center gap-2">
              {role ? (
                <span className="hidden rounded-pill border border-border px-3 py-1 text-xs text-text-muted md:inline">
                  {role}
                </span>
              ) : null}
            </div>
          </header>
          <main className="px-4 py-6 pb-24 md:px-6 md:pb-6">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
